(() => {
  const viewer = () => document.querySelector('[data-fizo-media-viewer]');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const normaliseItem = (item) => ({
    src: item.src,
    alt: item.alt || '',
    type: item.type || 'image',
    poster: item.poster || item.src,
  });

  const update = (node, index) => {
    const items = node._items || [];
    if (!items.length) return;
    node._index = (index + items.length) % items.length;
    node.querySelectorAll('.fizo-media-viewer__slide.is-zoomed').forEach((slide) => {
      slide.classList.remove('is-zoomed');
    });
    node._zoomPanY = 0;
    node.querySelector('[data-fizo-media-viewer-track]').style.transform = `translateX(-${node._index * 100}%)`;
    node.querySelectorAll('[data-fizo-media-viewer-thumbnail]').forEach((thumb, thumbIndex) => {
      thumb.classList.toggle('is-active', thumbIndex === node._index);
    });
    node.querySelector('[data-fizo-media-viewer-prev]').disabled = items.length < 2;
    node.querySelector('[data-fizo-media-viewer-next]').disabled = items.length < 2;
  };

  const render = (node, items) => {
    const track = node.querySelector('[data-fizo-media-viewer-track]');
    const thumbnails = node.querySelector('[data-fizo-media-viewer-thumbnails]');
    track.innerHTML = '';
    thumbnails.innerHTML = '';
    items.forEach((item, index) => {
      const slide = document.createElement('div');
      slide.className = 'fizo-media-viewer__slide';
      if (item.type === 'video') {
        const video = document.createElement('video');
        video.src = item.src;
        video.poster = item.poster;
        video.autoplay = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        slide.append(video);
      } else {
        const image = document.createElement('img');
        image.src = item.src;
        image.alt = item.alt;
        image.draggable = false;
        slide.append(image);
      }
      track.append(slide);

      const thumbnail = document.createElement('button');
      thumbnail.type = 'button';
      thumbnail.className = 'fizo-media-viewer__thumbnail';
      thumbnail.dataset.fizoMediaViewerThumbnail = String(index);
      thumbnail.setAttribute('aria-label', `View image ${index + 1}`);
      const preview = document.createElement('img');
      preview.src = item.poster;
      preview.alt = '';
      thumbnail.append(preview);
      thumbnail.addEventListener('click', () => update(node, index));
      thumbnails.append(thumbnail);
    });
  };

  const close = () => {
    const node = viewer();
    if (!node?.open) return;
    window.clearTimeout(node._closeTimer);
    node.classList.remove('is-active');
    node._closeTimer = window.setTimeout(() => node.close(), reduceMotion ? 0 : 500);
  };

  const open = (rawItems, startIndex = 0) => {
    const node = viewer();
    const items = rawItems.map(normaliseItem).filter((item) => item.src);
    if (!node || !items.length) return;
    window.clearTimeout(node._closeTimer);
    node._items = items;
    render(node, items);
    update(node, startIndex);
    if (!node.open) node.showModal();
    window.requestAnimationFrame(() => node.classList.add('is-active'));
  };

  const collectFizoItems = (trigger) => {
    const scope = trigger.closest('[data-fizo-preview-scope]') || document;
    return [...scope.querySelectorAll('[data-fizo-preview-item]')].map((item) => ({
      src: item.dataset.fizoPreviewSrc,
      alt: item.dataset.fizoPreviewAlt || '',
      type: item.dataset.fizoPreviewType || 'image',
      poster: item.dataset.fizoPreviewPoster || item.dataset.fizoPreviewSrc,
    }));
  };

  const collectProductItems = (trigger) => {
    const gallery = trigger.closest('media-gallery') || trigger.closest('.product__media-wrapper');
    return [...gallery.querySelectorAll('.product__modal-opener--image img')].map((image) => ({
      src: image.currentSrc || image.src,
      alt: image.alt || '',
    }));
  };

  const init = () => {
    const node = viewer();
    if (!node || node.dataset.fizoMediaViewerReady) return;
    node.dataset.fizoMediaViewerReady = 'true';
    let pointerStart;
    let suppressImageClick = false;

    node.querySelector('[data-fizo-media-viewer-close]').addEventListener('click', close);
    node.querySelector('[data-fizo-media-viewer-prev]').addEventListener('click', () => update(node, node._index - 1));
    node.querySelector('[data-fizo-media-viewer-next]').addEventListener('click', () => update(node, node._index + 1));
    node.addEventListener('click', (event) => {
      if (event.target === node) close();
      const image = event.target.closest('.fizo-media-viewer__slide img');
      if (!image) return;
      if (suppressImageClick) {
        suppressImageClick = false;
        return;
      }
      const slide = image.closest('.fizo-media-viewer__slide');
      slide.classList.toggle('is-zoomed');
      node._zoomPanY = 0;
      image.style.setProperty('--fizo-zoom-pan-y', '0px');
    });
    node.addEventListener('pointermove', (event) => {
      const image = event.target.closest('.fizo-media-viewer__slide.is-zoomed img');
      if (!image) return;
      const bounds = image.getBoundingClientRect();
      image.style.setProperty('--fizo-zoom-x', `${((event.clientX - bounds.left) / bounds.width) * 100}%`);
      image.style.setProperty('--fizo-zoom-y', `${((event.clientY - bounds.top) / bounds.height) * 100}%`);
    });
    node.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; });
    node.addEventListener('pointerup', (event) => {
      if (pointerStart == null || Math.abs(event.clientX - pointerStart) < 48) return;
      suppressImageClick = true;
      update(node, node._index + (event.clientX < pointerStart ? 1 : -1));
      pointerStart = null;
    });
    node.addEventListener('wheel', (event) => {
      const image = event.target.closest('.fizo-media-viewer__slide.is-zoomed img');
      if (!image || Math.abs(event.deltaY) < 1) return;
      event.preventDefault();
      const slide = image.closest('.fizo-media-viewer__slide');
      const overflow = Math.max(0, (image.getBoundingClientRect().height - slide.clientHeight) / 2);
      node._zoomPanY = Math.max(-overflow, Math.min(overflow, (node._zoomPanY || 0) - event.deltaY * .45));
      image.style.setProperty('--fizo-zoom-pan-y', `${node._zoomPanY}px`);
    }, {passive: false});
    node.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') update(node, node._index - 1);
      if (event.key === 'ArrowRight') update(node, node._index + 1);
    });
    node.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    document.addEventListener('click', (event) => {
      const customTrigger = event.target.closest('[data-fizo-preview-item]');
      if (customTrigger) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const items = collectFizoItems(customTrigger);
        open(items, [...(customTrigger.closest('[data-fizo-preview-scope]') || document).querySelectorAll('[data-fizo-preview-item]')].indexOf(customTrigger));
        return;
      }

      const productTrigger = event.target.closest('.product__modal-opener--image');
      if (!productTrigger) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const triggers = [...(productTrigger.closest('media-gallery') || productTrigger.closest('.product__media-wrapper')).querySelectorAll('.product__modal-opener--image')];
      open(collectProductItems(productTrigger), triggers.indexOf(productTrigger));
    }, true);
  };

  window.FizoMediaViewer = {open, close};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();
